import React, { useMemo } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import { FormattedMessage, useIntl } from "react-intl";
import { documentLink } from "@/components/document-link";
import Link from "@/components/link";
import ArrowUpIcon from "@/icons/arrow-up";
import InstagramIcon from "@/icons/instagram";
import LinkedinIcon from "@/icons/linkedin";

type FooterMenuProps = {
  title: string;
  url?: string;
  links?: Array<LinkType>;
};

const FooterMenu: React.FC<FooterMenuProps> = ({
  title,
  url = undefined,
  links = undefined,
}) => (
  <div>
    <h3>{url ? <Link to={url}>{title}</Link> : title}</h3>
    {links && (
      <ul className="space-y-3">
        {links.map(({ title, url }) => (
          <li key={title}>
            <Link
              className="transition-colors text-theme-800 hover:text-theme-900"
              to={url}
            >
              {title}
            </Link>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const SocialLink: React.FC<{
  url: string | undefined | null;
  title: string;
  icon: React.JSXElementConstructor<React.HtmlHTMLAttributes<SVGElement>>;
}> = ({ url, title, icon: Icon }) => {
  if (!url) return null;

  return (
    <li>
      <Link
        to={url}
        className="inline-flex items-center gap-2 transition-colors text-theme-800 hover:text-theme-900"
      >
        <Icon className="size-[1.375rem]" />
        <span>{title}</span>
      </Link>
    </li>
  );
};

const Footer: React.FC = () => {
  const { locale } = useIntl();

  const { settings } = useStaticQuery<Queries.FooterQuery>(graphql`
    query Footer {
      settings: sanitySettings {
        footerNavigation {
          ...InternationalizedDocumentReferenceNestedList
        }
        instagram
        linkedin
      }
    }
  `);

  const navigation = useMemo(
    () =>
      settings?.footerNavigation
        ?.find((item) => item?.locale === locale)
        ?.value?.filter((item) => !!item)
        .map((item, i) => {
          if ("children" in item!) {
            return {
              id: item?.id ?? i.toString(),
              title: item.title!,
              links:
                item.children
                  ?.filter((child) => !!child)
                  ?.map((child, j) => ({
                    id: item?.id ?? j.toString(),
                    ...documentLink(child!),
                  })) ?? [],
            };
          }

          return {
            id: item?.id ?? i.toString(),
            ...documentLink(item!),
          };
        }),
    [settings, locale],
  );

  const scrollToTop = () => window.scroll({ top: 0, behavior: "smooth" });

  return (
    <footer className="text-sm-fluid mt-lg md:text-base-fluid">
      <div className="mx-auto max-w-screen-max px-safe-offset-base">
        <div className="grid grid-cols-1 py-lg gap-base sm:grid-cols-2 xl:grid-cols-4">
          {navigation &&
            navigation.map(({ id, ...props }) => (
              <FooterMenu key={id} {...props} />
            ))}
          {Boolean(settings?.linkedin || settings?.instagram) && (
            <div>
              <h3>
                <FormattedMessage id="follow_us" defaultMessage="Follow us" />
              </h3>
              <ul className="space-y-3">
                <SocialLink
                  url={settings?.linkedin}
                  title="LinkedIn"
                  icon={LinkedinIcon}
                />
                <SocialLink
                  url={settings?.instagram}
                  title="Instagram"
                  icon={InstagramIcon}
                />
              </ul>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 border-t mt-lg border-theme-200/50 py-lg gap-xl xl:grid-cols-4 xl:gap-base">
          <div className="xl:col-span-2">
            <div className="mb-6 text-sm text-theme-800 max-w-prose">
              <FormattedMessage
                id="footer_funding"
                defaultMessage="Funded with resources from zukunft.niedersachsen, a funding program of the Lower Saxony Ministry of Science and Culture and VolkswagenStiftung."
              />
            </div>
            <div className="flex flex-wrap items-center gap-md">
              <Link
                to="https://zukunft.niedersachsen.de/"
                aria-label="Zukunft Niedersachsen"
              >
                <svg
                  height="58"
                  viewBox="0 0 204 58"
                  width="204"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-auto h-10 md:h-12 xl:h-14"
                >
                  <g fillRule="evenodd">
                    <path d="m4.2407778 56.5232389h-4.24070098v-15.6742515h4.11419698v1.9434589c.95774286-1.6238111 2.83942588-2.3584897 4.53007249-2.3584897 3.89268691 0 5.67885171 2.7735204 5.67885171 6.2119083v9.877374h-4.2435178v-9.1426954c0-1.752693-.86222469-3.1241101-2.90395832-3.1241101-1.84813641 0-2.93494408 1.4358581-2.93494408 3.2504349zm12.620362.0001022h4.240701v-15.6742514h-4.240701zm2.1034491-24.164098c1.4688805 0 2.6176598 1.1806512 2.6176598 2.6446383 0 1.402359-1.1487793 2.5801974-2.6176598 2.5801974-1.4350778 0-2.6145868-1.1778384-2.6145868-2.5801974 0-1.4639871 1.179509-2.6446383 2.6145868-2.6446383zm30.0224472 20.7537625c2.1710545 0 3.8617012-1.654753 3.8617012-4.4899015 0-2.8044622-1.6906467-4.3666452-3.8617012-4.3666452-2.1682376 0-3.8924308 1.5928692-3.8924308 4.3973314 0 2.8044623 1.6596608 4.4592153 3.8924308 4.4592153zm7.9758981.5748546c0 1.0517693.0647885 2.1986657.1293209 2.8354042h-4.0527375c-.0645324-.3196478-.1600506-1.0824554-.1600506-1.8173897-.701918 1.2425351-2.2973025 2.1986657-4.4035685 2.1986657-4.4655401 0-7.6557969-3.5025732-7.6557969-8.2507501 0-4.5880972 3.0947386-8.186309 7.5292929-8.186309 2.7131779 0 3.9882051 1.1133974 4.4345543 1.9437146v-9.3986694h4.1789854zm30.5336617-8.6349411c-.477335-.0953829-.8932105-.1263249-1.2750272-.1263249-2.1682376 0-4.0527375 1.0517693-4.0527375 4.4282734v7.1682945h-4.240701v-15.6742514h4.114453v2.3249905c.9577429-2.0697837 3.1257244-2.4510596 4.4655401-2.4510596.350831 0 .6711883.0306862.9884726.0616281zm4.7547323 6.3100785c.0955182 1.2422793 1.0222753 2.3866186 2.8729725 2.3866186 1.4043481 0 2.0727194-.7318657 2.0727194-1.55937 0-.7009238-.4773349-1.2732213-1.6906466-1.5284281l-2.0727194-.4794718c-3.0302062-.6674247-4.4035685-2.4848144-4.4035685-4.6834801 0-2.8016494 2.4883389-5.1266399 5.8696321-5.1266399 4.4683569 0 5.9651503 2.8354042 6.1590033 4.5236562l-3.5415997.7965624c-.1290648-.9257001-.7974361-2.1035385-2.5836009-2.1035385-1.1177935 0-2.0110039.6674247-2.0110039 1.55937 0 .7656205.5728531 1.2422794 1.4350778 1.402359l2.23277.4794718c3.0949947.6364828 4.6591377 2.5155006 4.6591377 4.8095492 0 2.5492555-1.9797624 5.1603947-6.0942155 5.1603947-4.7211089 0-6.3472231-3.0568562-6.5408203-4.8404911zm18.7324008 1.9406971c2.198967 0 3.858884-1.8145769 3.858884-4.6806673 0-2.8689033-1.626114-4.557411-3.858884-4.557411-2.266317 0-3.926234 1.6885077-3.926234 4.557411 0 2.8354042 1.628931 4.6806673 3.926234 4.6806673zm3.889613 1.0852683c-.668371 1.4977419-2.294485 2.5801974-4.400751 2.5801974-4.625591 0-7.6896-3.6010247-7.6896-8.346133 0-4.5880972 2.999477-8.2200638 7.560535-8.2200638 2.80844 0 4.117014 1.4977419 4.499087 2.3894315v-1.9434588h4.114197v12.8388472c0 1.402359.095518 2.4848145.129321 2.8354042h-4.052482c-.064788-.3505897-.160307-1.1806512-.160307-1.9434589zm10.944645-5.7042308c0 2.8354042 1.850698 4.4282734 4.018935 4.4282734 2.170799 0 3.255046-1.4330452 3.606133-2.6446384l3.73238 1.2422794c-.702174 2.6139521-3.159527 5.2892767-7.338513 5.2892767-4.62559 0-8.262453-3.5028289-8.262453-8.3151911 0-4.8404911 3.57233-8.3123781 8.135949-8.3123781 4.274504 0 6.69831 2.6418255 7.369499 5.2864638l-3.797169 1.2760342c-.381817-1.3069761-1.370289-2.6446383-3.476812-2.6446383-2.170798 0-3.987949 1.5591143-3.987949 4.3945184m18.09207 7.8385067h-4.240701v-23.5107379h4.240701v9.1434625c1.022275-1.2115932 2.743908-1.7220069 4.243518-1.7220069 3.987949 0 5.838902 2.7735204 5.838902 6.2119083v9.877374h-4.243517v-9.1426954c0-1.752693-.862225-3.1241101-2.903959-3.1241101-1.786165 0-2.839426 1.3376622-2.934944 3.0596691zm16.047519-5.1602413c.095519 1.2422793 1.022276 2.3866186 2.872973 2.3866186 1.404348 0 2.072719-.7318657 2.072719-1.55937 0-.7009238-.477335-1.2732213-1.690646-1.5284281l-2.07272-.4794718c-3.030206-.6674247-4.403568-2.4848144-4.403568-4.6834801 0-2.8016494 2.488339-5.1266399 5.869632-5.1266399 4.468357 0 5.96515 2.8354042 6.159004 4.5236562l-3.5416.7965624c-.129065-.9257001-.797437-2.1035385-2.583601-2.1035385-1.117794 0-2.011004.6674247-2.011004 1.55937 0 .7656205.572853 1.2422794 1.435078 1.402359l2.23277.4794718c3.094994.6364828 4.659137 2.5155006 4.659137 4.8095492 0 2.5492555-1.979762 5.1603947-6.094215 5.1603947-4.721109 0-6.347223-3.0568562-6.540821-4.8404911zm32.960055 5.1602413h-4.240701v-15.6742515h4.114197v1.9434589c.957743-1.6238111 2.839426-2.3584897 4.530072-2.3584897 3.892687 0 5.678852 2.7735204 5.678852 6.2119083v9.877374h-4.243518v-9.1426954c0-1.752693-.862224-3.1241101-2.903958-3.1241101-1.848136 0-2.934944 1.4358581-2.934944 3.2504349zm-161.3176994-12.7127015c2.4238065 0 3.4460817 1.531241 3.5077973 3.090611h-7.0184114c.0955182-1.4330451 1.3088299-3.090611 3.5106141-3.090611zm2.7746374 8.7995215c-.5938518.4684758-1.3766913.7582046-2.4235504.7582046-2.106266 0-3.8617011-1.4977419-3.9572193-3.5672699h11.2311995c0-.0646967.0645324-.7011795.0645324-1.306976 0-5.0340699-2.9039583-8.1218681-7.754132-8.1218681-4.0189348 0-7.7205854 3.2476221-7.7205854 8.2507501 0 5.2864638 3.7971688 8.3770748 8.1024022 8.3770748 2.0909011 0 3.763366-.6758634 4.9987006-1.7138238zm29.6405793-5.7089872c.0955182-1.4330452 1.3088298-3.090611 3.5106141-3.090611 2.4235503 0 3.4458256 1.531241 3.5077972 3.090611zm3.4460817-6.5287432c-4.0189349 0-7.7205854 3.2476221-7.7205854 8.2507501 0 5.2864638 3.7971687 8.3768191 8.1024021 8.3768191 2.0309782 0 3.6698965-.6346927 4.895244-1.6215095l-2.5382747-2.6727674c-.5805356.4170765-1.3410961.662566-2.3231666.662566-2.1065221 0-3.8617011-1.4974862-3.9572193-3.5672699h11.2309434c0-.064441.0647885-.7011795.0647885-1.3069761 0-5.0340698-2.9039583-8.1216123-7.754132-8.1216123zm107.30259 6.5287432c.095518-1.4330452 1.30883-3.090611 3.510614-3.090611 2.423551 0 3.445826 1.531241 3.507797 3.090611zm3.446082-6.5287432c-4.018935 0-7.720586 3.2476221-7.720586 8.2507501 0 5.2864638 3.797169 8.3768191 8.102402 8.3768191 2.030979 0 3.669897-.6346927 4.895244-1.6215095l-2.538274-2.6727674c-.580536.4170765-1.341096.662566-2.323167.662566-2.106522 0-3.861701-1.4974862-3.957219-3.5672699h11.230943c0-.064441.064789-.7011795.064789-1.3069761 0-5.0340698-2.903959-8.1216123-7.754132-8.1216123z" />
                    <path d="m0 25.169v-1.191l10.663-13.291h-10.596v-1.193h12.185v1.193l-10.66 13.291h10.794v1.191z" />
                    <path d="m21.4951299 25.5676471c-4.2063861 0-5.8942158-3.0768023-5.8942158-6.2525675v-9.82111592h1.3234265v9.62319002c0 2.7129151 1.2914163 5.2570562 4.7019028 5.2570562 2.9472359 0 4.8680994-2.4132133 4.8680994-5.2220228v-9.65822342h1.3234264v12.63299402c0 1.1236261.0991034 2.2822856.1982067 3.0420246h-1.2563333c-.035083-.2971446-.1661965-1.1906243-.1661965-2.4132133v-.3638871c-.6297031 1.4524797-2.2852666 3.1757652-5.0983162 3.1757652" />
                    <path d="m38.148 16.177 6.889 8.992h-1.656l-6.124-8.101-3.28 3.272v4.829h-1.323v-23.52h1.323v16.938l9.074-9.093h1.819z" />
                    <path d="m53.4842278 25.5676471c-4.2063861 0-5.8942158-3.0768023-5.8942158-6.2525675v-9.82111592h1.3234264v9.62319002c0 2.7129151 1.2914164 5.2570562 4.7019029 5.2570562 2.9472359 0 4.8680993-2.4132133 4.8680993-5.2220228v-9.65822342h1.3234265v12.63299402c0 1.1236261.0991034 2.2822856.1982067 3.0420246h-1.2563333c-.0350831-.2971446-.1661965-1.1906243-.1661965-2.4132133v-.3638871c-.6297031 1.4524797-2.2852667 3.1757652-5.0983162 3.1757652m12.4528594-13.194935c.6617133-1.6534743 2.4834733-3.27472816 5.2325026-3.27472816 3.7077965 0 5.5618228 2.57891876 5.5618228 5.68794146v10.382929h-1.3234265v-10.185003c0-2.6111393-1.5247062-4.6924303-4.6027995-4.6924303-2.8829597 0-4.8360893 2.5791745-4.8360893 5.3209858v9.5564475h-1.3234265v-15.67501858h1.2914164zm31.746538-1.685388v-1.19343714h-4.5036961v-4.98931919h-1.2243232v2.71010224c0 1.72021681-.7288065 2.27921695-2.2181734 2.27921695h-1.4161278-.009475-4.6924278v-3.56880419c0-2.64591696 1.3905197-3.73757826 3.2793729-3.73757826.6617133 0 1.1923131.13399637 1.5887264.33192232v-1.19062428c-.5305998-.22989072-1.0611995-.32885369-1.7549229-.32885369-2.6176598 0-4.4366029 1.58340753-4.4366029 4.82617093v3.66776717h-3.4104865v1.19343714h3.4104865v14.4815815h1.3234265v-14.4815815h4.6924278.009475 3.5351979v10.2811531c0 2.8760634 1.5216331 4.3663895 3.9410862 4.3663895.89193 0 1.5887264-.1659611 1.8860364-.2968889v-1.1586595c-.2332898.098963-.895003.2649242-1.5567163.2649242-1.8857803 0-2.9469798-1.0274761-2.9469798-3.2396948v-10.2172236zm3.9183458 11.8035463c.7959 0 1.425347.6257426 1.425347 1.4233279 0 .7569261-.629447 1.3857373-1.425347 1.3857373-.760816 0-1.390519-.6288112-1.390519-1.3857373 0-.7975853.629703-1.4233279 1.390519-1.4233279" />
                  </g>
                </svg>
              </Link>
              <Link
                to="https://www.mwk.niedersachsen.de"
                aria-label="Niedersächsisches Ministerium für Wissenschaft und Kultur"
              >
                <StaticImage
                  src="../../../resources/images/nmwk.png"
                  height={44}
                  className="max-w-48 md:max-w-56 lg:max-w-none"
                  placeholder="blurred"
                  alt="Logo Niedersächsisches Ministerium für Wissenschaft und Kultur"
                />
              </Link>
            </div>
          </div>
          <div>
            <div className="mb-6 text-sm text-theme-800 max-w-prose">
              <FormattedMessage
                id="footer_image_source"
                defaultMessage="Aerial photographs from the geodata of the State Office for Geoinformation and Surveying of Lower Saxony"
              />
            </div>
            <Link to="https://www.lgln.niedersachsen.de" aria-label="LGLN">
              <svg
                height="32"
                viewBox="0 0 105 32"
                width="105"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g fill="none" fillRule="evenodd">
                  <g fill="#5f6164">
                    <path d="m37.408 18.492v13.36h13.249v-3.434h-9.172v-9.926z" />
                    <path d="m52.1785554 27.1668805c0 3.910034 2.1849039 4.8332815 5.8635112 4.8332815h4.9045408c4.5948312 0 6.5797145-.1862693 6.5797145-4.025035v-3.8007021h-9.1525619v2.7681227h5.0723001v.3895456c0 1.2901169-1.0678527 1.4561394-2.1340923 1.4561394h-4.3536772c-2.0574714 0-2.5897846-.4964479-2.5897846-2.7867495v-1.658606c0-2.2886819.5323132-2.7883693 2.5897846-2.7883693h4.4270719c1.3082002 0 2.0606976.1660226 2.0606976 1.495013h4.0044474v-.42356c0-2.6012903-.6411955-4.2825725-5.275547-4.2825725h-6.1328939c-3.6786073 0-5.8635112.9248672-5.8635112 4.8357111z" />
                    <path d="m71.984 31.852h13.251v-3.434h-9.17v-9.925h-4.081z" />
                    <path d="m15.44 0-15.44 8.96 11.861 20.657 10.306-5.973-2.922-5.139-5.169 2.97-2.993-5.137 5.152-3.005-2.992-5.155 5.152-3.024z" />
                    <path d="m18.395 5.154 5.153-2.968 8.922 15.52-5.151 2.954-2.958-5.141-5.118 2.986-3.008-5.172 5.152-2.969z" />
                  </g>
                  <path
                    d="m27.319 20.66-5.153 2.985 2.959 5.146 5.138-2.986z"
                    fill="#c4153a"
                  />
                  <path
                    d="m86.901 31.866h4.078v-9.707h.036l7.663 9.707h6.322v-13.36h-4.078v9.707h-.036l-7.667-9.707h-6.318z"
                    fill="#5f6164"
                  />
                </g>
              </svg>
            </Link>
          </div>
          <div className="text-right">
            <a
              href="#top"
              className="inline-flex items-center gap-2 text-theme-800 hover:text-theme-900"
              onClick={(e) => {
                e.preventDefault();
                scrollToTop();
              }}
            >
              <ArrowUpIcon />
              <span>
                <FormattedMessage
                  id="scroll_to_top"
                  defaultMessage="Scroll to top"
                />{" "}
              </span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
